import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CellAlert, WSClientMsgCellAlertsMsg } from '@liveline/ll_node_common/lib/models/websocket';
import { RootState } from '../state/features/reducers';
import { addCellAlerts, clearCellAlerts } from '../state/features/websocketReducer';
import { WarningNotification, FailedNotification } from '../components/shared/notification/NotificationComponent';
import useCanViewCellTuning from './useCanViewCellTuning';
import { buildCellAlertsTopic, sanitizeTopicFilters } from '../utils/kafkaTopics';

const HEARTBEAT_MS = 30000;
const RECONNECT_MS = 10000;

/**
 * Audiences the current user may see. operator+engineer for everyone;
 * controls/internal require the LivelineControls screen (v1 simplification —
 * both are Liveline-staff audiences today).
 */
export const useVisibleAudiences = (): string[] => {
    const canControls = useCanViewCellTuning();
    return useMemo(
        () => (canControls ? ['operator', 'engineer', 'controls', 'internal'] : ['operator', 'engineer']),
        [canControls],
    );
};

/** Membership test — audience is an OPEN set; unknown values never break filtering. */
export const isAlertVisible = (alert: CellAlert, visibleAudiences: string[]): boolean => {
    if (!Array.isArray(alert.audience) || alert.audience.length === 0) return true; // defensive
    return alert.audience.some((a) => visibleAudiences.includes(a));
};

/** Liveline-staff audiences — hard-gated by role, never exposed as opt-in chips. */
export const RESTRICTED_AUDIENCES = ['controls', 'internal'];

/**
 * Hard role gate: block an alert only when EVERY audience on it is a
 * restricted one the user lacks. Unknown audiences (open set, e.g. a future
 * "quality") pass the gate — they surface as default-off chips in the drawer
 * instead of vanishing entirely.
 */
export const isAlertRoleBlocked = (alert: CellAlert, visibleAudiences: string[]): boolean => {
    if (!Array.isArray(alert.audience) || alert.audience.length === 0) return false; // defensive
    return !alert.audience.some(
        (a) => !RESTRICTED_AUDIENCES.includes(a) || visibleAudiences.includes(a),
    );
};

/**
 * Maintains a DEDICATED websocket connection for the CellAlerts live feed
 * (LT-3052), independent of the shared per-facility signal-data connection so
 * page mount/unmount can never tear the alert feed down. Subscribes to
 * CellAlerts_{cellId} for every cell of the selected facility (both the
 * OnPrem.-mirrored and cloud-native topic names), dispatches incoming alerts
 * into the websocket redux slice, and fires toasts for live warning/critical
 * alerts the user is allowed to see.
 */
export const useCellAlerts = () => {
    const dispatch = useDispatch();
    const socketURL = useSelector((s: RootState) => s.auth?.user?.socketURL);
    const token = useSelector((s: RootState) => s.auth?.user?.accessToken);
    const facility = useSelector((s: RootState) => s.userSelection?.facility);
    const cells = useSelector((s: RootState) => s.cellList?.data);
    const visibleAudiences = useVisibleAudiences();

    const facilityId: number = facility?.id ?? 0;
    const isCloudOnly: boolean = Boolean((facility as any)?.isCloudOnly);
    const cellIds = useMemo(() => (cells ?? []).map((c) => c.id).filter(Boolean), [cells]);
    // id → display name ("Line 5"), so the footer can name a cell the way the
    // rest of the console does instead of echoing the raw facility_line_id.
    const cellNames = useMemo(() => {
        const map: Record<number, string> = {};
        (cells ?? []).forEach((c) => {
            if (c?.id && c?.name) map[c.id] = c.name;
        });
        return map;
    }, [cells]);

    const alerts = useSelector((s: RootState) => s.websocket.cellAlerts[facilityId]) ?? [];

    const socketRef = useRef<WebSocket | null>(null);
    const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closedRef = useRef<boolean>(false);
    const visibleAudiencesRef = useRef(visibleAudiences);
    visibleAudiencesRef.current = visibleAudiences;
    const cellIdsRef = useRef(cellIds);
    // Token lives in a ref so the recurring Redux accessToken refresh (the
    // LT-2973 client-churn pattern) can NOT bounce the socket — reconnects
    // read the freshest token at open time instead.
    const tokenRef = useRef(token);
    tokenRef.current = token;

    // Topics are built via buildCellAlertsTopic and sanitized before the send (LT-3186).
    // `cellIds` is already `.filter(Boolean)`-ed, so nothing should ever be dropped here —
    // this hook owns its own socket and so bypasses the websocketService backstop, and the
    // guarantee that no "CellAlerts_undefined" can reach the wire has to hold here too.
    const sendAlertFilters = (socket: WebSocket, ids: number[], cloudOnly: boolean) => {
        if (socket.readyState !== WebSocket.OPEN || ids.length === 0) return;
        const prefixes = cloudOnly ? [''] : ['OnPrem.', ''];
        const filters = sanitizeTopicFilters(
            ids
                .flatMap((id) => prefixes.map((p) => buildCellAlertsTopic(p, id)))
                .filter((topic): topic is string => topic !== null)
                .map((kafkaTopic) => ({ kafkaTopic, kafkaPartition: 0 })),
            'cellAlerts',
        );
        if (!filters.length) return;
        try {
            socket.send(JSON.stringify(filters));
        } catch (e) {
            console.error('cellAlerts: failed to send filters', e);
        }
    };

    // Connection lifecycle — per facility
    useEffect(() => {
        if (!facilityId || !socketURL) return;
        closedRef.current = false;

        const buildUrl = (): string => {
            let url = socketURL;
            if (process.env.REACT_APP_STAGE !== 'prod-internal') {
                url += `/${facilityId}`;
                if (tokenRef.current) url += `?token=${tokenRef.current}`;
            }
            return url;
        };

        const open = () => {
            if (closedRef.current) return;
            let socket: WebSocket;
            try {
                socket = new WebSocket(buildUrl());
            } catch (e) {
                console.error('cellAlerts: websocket open failed', e);
                reconnectRef.current = setTimeout(open, RECONNECT_MS);
                return;
            }
            socketRef.current = socket;

            socket.onopen = () => {
                sendAlertFilters(socket, cellIdsRef.current, isCloudOnly);
                if (heartbeatRef.current) clearInterval(heartbeatRef.current);
                heartbeatRef.current = setInterval(() => {
                    try {
                        if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'hb' }));
                    } catch {
                        /* heartbeat failures surface via onclose */
                    }
                }, HEARTBEAT_MS);
            };

            socket.onmessage = (event: MessageEvent) => {
                try {
                    const msg = JSON.parse(event.data) as WSClientMsgCellAlertsMsg;
                    if (msg?.type !== 'cellAlerts' || !Array.isArray(msg.alerts)) return;
                    dispatch(addCellAlerts({ facilityId, alerts: msg.alerts, snapshot: msg.snapshot }));
                    if (!msg.snapshot) {
                        msg.alerts
                            .filter((a) => a.state !== 'cleared' && isAlertVisible(a, visibleAudiencesRef.current))
                            .forEach((a) => {
                                if (a.severity === 'critical') FailedNotification(a.code, a.message);
                                else if (a.severity === 'warning') WarningNotification(a.code, a.message);
                            });
                    }
                } catch {
                    /* non-JSON or non-alert traffic on this socket is ignored */
                }
            };

            socket.onclose = () => {
                if (heartbeatRef.current) {
                    clearInterval(heartbeatRef.current);
                    heartbeatRef.current = null;
                }
                if (!closedRef.current) {
                    reconnectRef.current = setTimeout(open, RECONNECT_MS);
                }
            };
            socket.onerror = () => {
                /* onclose follows and schedules the reconnect */
            };
        };

        open();

        return () => {
            closedRef.current = true;
            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            try {
                socketRef.current?.close();
            } catch {
                /* already closed */
            }
            socketRef.current = null;
            dispatch(clearCellAlerts(facilityId));
        };
        // token intentionally NOT a dep — see tokenRef above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, socketURL, isCloudOnly, dispatch]);

    // Re-send filters when the facility's cell list changes while connected
    useEffect(() => {
        cellIdsRef.current = cellIds;
        const socket = socketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
            sendAlertFilters(socket, cellIds, isCloudOnly);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellIds.join(','), isCloudOnly]);

    return { alerts, facilityId, visibleAudiences, cellNames };
};

export default useCellAlerts;
